<template>
  <div>
    <VModal ref="modal" :showing="showing" max-content="calc(100vh - 234px)" @update:showing="$emit('update:showing', $event)">
      <template #header>
        <h2 class="mb-2 text-xl">3D Presentation Settings</h2>
        <p class="m-0 text-sm font-light text-gray-800">Update this 3D Presentation by filling in the information below.</p>
      </template>

      <template #default>
        <LoadingSpinner
          v-if="loading"
          class="absolute z-10 top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 transform"
          :show-background="true"
        />
        <form class="space-y-4" @submit.prevent="update">
          <div class="flex-col items-start flex-1 space-y-2 form-field plain">
            <div class="flex items-end form-labels"><div class="flex-1" /></div>
            <div class="flex-1 space-y-2">
              <label class="inline-block mb-0 text-sm font-medium text-gray-900">3D Presentation Image</label>
              <EditorDeckFileUploader
                :folder="folder"
                :original-file="file"
                @file-edited="showImageEditorModal = true"
                @file-uploaded="onFileUploaded"
                @file-replaced="setPreviewFile(null)"
                @file-changed="showFileSelectorModal = true"
              />
              <p v-if="shelfError" class="text-xs text-red-800 error">{{ shelfError.message }}</p>
              <p class="text-xs text-gray-500" type="info">Recommended Size is 900x600 pixels.</p>
            </div>
          </div>

          <div class="flex gap-4">
            <FormField
              v-model="formTitle"
              :attrs="titleAttrs"
              class="flex-1"
              label="3D Presentation Title"
              required
              :error="errors.title"
              :maxlength="255"
            />
            <FormField class="w-64" label="3D Presentation Type">
              <VDropdown v-model="deckType as Option" class="w-full" size="sm" :options="DECK_TYPES as Option[]">
                <template #current-single="{ item }">{{ item.name }}</template>
                <template #option="{ item }">
                  <div class="px-3 py-2 hover_bg-gray-200 transition-color duration-150">{{ item.name }}</div>
                </template>
              </VDropdown>
            </FormField>
          </div>

          <FormField label="Description">
            <VInput v-model="form.description as string" type="textarea" placeholder="Describe this 3D Presentation…" rows="6" :maxlength="255" />
          </FormField>

          <div class="space-y-1">
            <div class="flex items-center justify-between w-full">
              <div>
                <label class="block mb-0 text-sm">Display 3D Layer Menu</label>
                <label class="mb-0 text-xs font-normal">Allow viewers of my 3D Presentation to view all the layers in my 3D models</label>
              </div>
              <VSwitch :value="form.show_3d_menu" @input="form.show_3d_menu = $event" />
            </div>
          </div>

          <div v-if="isInternalAdmin" class="flex w-full">
            <FormField
              v-model="formUrlSlug"
              :attrs="urlSlugAttrs"
              class="w-full gold"
              label="Custom URL"
              :error="errors.url_slug"
              :maxlength="255"
            >
              <template #error>The URL slug can only contain alphanumeric characters or a dash.</template>
            </FormField>
          </div>

          <div v-if="isInternalAdmin" class="flex gap-4">
            <FormField
              v-model="form.press_release_headline as string"
              class="flex-1 gold"
              label="Press Release Headline"
              placeholder="Press Release Headline"
            />
            <FormField class="gold" label="Press Release Date" placeholder="Press Release Headline" :maxlength="255">
              <VDatePicker v-model="pressReleaseDate as Date" />
            </FormField>
          </div>
          <!---->
          <!-- Related Asset Selection-->
          <!---->
          <FormField v-if="assets.length || assetsLoading || assetsError" label="Related Assets">
            <LoadingSpinner v-if="assetsLoading" class="mix-blend-difference" size="sm" :transparent-container="true" />
            <div v-else-if="assetsError" class="error">There was an error retrieving company assets.</div>
            <div v-else class="px-2 overflow-y-auto border border-gray-300 grid grid-cols-3 col-gap-4 rounded-md shadow-sm max-h-60">
              <div v-for="asset in assets" :key="asset.id" class="flex items-center py-2 gap-2">
                <VCheckbox
                  :id="`input-${asset.id}`"
                  :model-value="getSelectedAssets(asset)"
                  :name="asset.name"
                  class="flex-shrink-0"
                  @input="$event ? addAsset(asset) : removeAsset(asset)"
                />
                <label class="m-0 cursor-pointer" :for="`input-${asset.id}`">{{ asset.name }}</label>
              </div>
            </div>
          </FormField>
          <FormField class="mt-1" label="Access Permission" required>
            <VRadioGroup
              v-slot="{ item: { value, description } }"
              :model-value="form.view_access_type"
              name="access-permission"
              :options="VIEW_ACCESS_OPTIONS"
              @update:model-value="(params, $event) => onUpdatePermissionSettings(params, $event)"
            >
              <div>
                <AccessPermissionBadge :value="value" />
                <div class="max-w-xl mt-1 text-xs font-normal leading-5">{{ description }}</div>
              </div>
            </VRadioGroup>
          </FormField>
        </form>
      </template>
      <template #footer>
        <div class="flex flex-row justify-end w-full gap-4">
          <VButton class="w-42" data-testid="cancel-button" @click="cancel">Cancel</VButton>
          <VButton class="w-42" :disabled="shouldDisableSubmit" variant="copper" data-testid="submit-button" @click="update">
            Update
          </VButton>
        </div>
      </template>
    </VModal>
    <EditorDeckModalsImageEditor
      v-if="file"
      :file="file"
      :showing="showImageEditorModal"
      @on-edits-completed="handleImageEdits"
      @update:showing="showImageEditorModal = $event"
    />
    <EditorDeckModalsFileSelector
      v-if="folder"
      :folder-id="folder.id"
      :company-id="deck.company_id"
      :showing="showFileSelectorModal"
      @on-file-selected="handleFileSelected"
      @close="showFileSelectorModal = false"
    />
    <EditorDeckModalsEmbedDeckUsage
      v-if="embedUsageWarning.showing"
      :showing="embedUsageWarning.showing"
      :decks-affected="embedUsageWarning.decksAffected"
      :public-decks="embedUsageWarning.publicDecks"
      @cancel="embedUsageWarning.cancel"
      @confirm="embedUsageWarning.confirm"
    />
    <EditorDeckModalsDrillDataAccessTypeWarning
      v-if="drillDataAccessTypeWarning.showing"
      :showing="drillDataAccessTypeWarning.showing"
      :drill-data="drillDataAccessTypeWarning.drillData"
      @cancel="drillDataAccessTypeWarning.cancel"
      @confirm="drillDataAccessTypeWarning.confirm"
      @secondary-action="drillDataAccessTypeWarning.secondaryAction"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { computed, reactive, watch, toRaw, toRef, ref } from 'vue'
import { z } from 'zod'
import { useForm } from 'vee-validate'
import ZodMessages from '@/zod_presets/messages'
import { toTypedSchema } from '@vee-validate/zod'

import type { Asset } from '~/entities/asset'
import type { Deck, IncludeAssets, Slide } from '~/entities/deck'
import type { DeckWithEmbed } from '~/entities/embed'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { DrillDataSet } from '~/entities/drillLabel'
import type { Drill } from '~/entities/drill'
import type { EditDeck } from '~/composables/useEditDeck'
import type { Option } from '~/components/VDropdown.vue'
import type { ShelfFile } from '~/entities/shelfFile'
import { DeckTypeTags } from '~/entities/deck'
import { FolderRequiredError, FileRequiredError } from '~/composables/useShelf'

const { $sentry } = useNuxtApp()

const VIEW_ACCESS_OPTIONS = [
  {
    value: 'public',
    name: 'Public',
    description:
      'Anyone can view this 3D Presentation on vrify.com and the VRIFY app. Public content can be embedded on a company website and will be viewable to all users. All Company Admins can edit Public 3D Presentations.',
  },
  {
    value: 'private',
    name: 'Private',
    description:
      'All Company Admins can view and make edits to this 3D Presentation. It will not be visible to any other users on vrify.com and the VRIFY app without a Secure link and password. Do not embed Private content on your company website; it will not be viewable.',
  },
  {
    value: 'selected',
    name: 'Secure',
    description:
      'Only manually selected Company Admins and/or VRIFY team members can view and make edits to this 3D Presentation. Secure content will not be visible to any unselected users on vrify.com or the VRIFY app without a Secure link and password. Do not embed Secure content on your company website; it will not be viewable.',
  },
].map(x => ({
  originalItem: x,
  name: x.name,
  value: x.value,
}))

const DECK_TYPES: Array<{ name: string; id: DeckTypeTags | null }> = [
  { name: 'None', id: null },
  { name: DeckTypeTags.Investor, id: DeckTypeTags.Investor },
  { name: DeckTypeTags.PressRelease, id: DeckTypeTags.PressRelease },
  { name: DeckTypeTags.Technical, id: DeckTypeTags.Technical },
  { name: DeckTypeTags.SiteVisit, id: DeckTypeTags.SiteVisit },
  { name: DeckTypeTags.ESG, id: DeckTypeTags.ESG },
]

type NullableStringKeys = 'description' | 'press_release_headline'

type WriteableKeys =
  | NullableStringKeys
  | (
      | 'file_id'
      | 'view_access_type'
      | 'edit_access_type'
      | 'press_release_date'
      | 'type_tags'
      | 'assets'
      | 'file'
      | 'deck_image'
      | 'thumbnail'
      | 'show_3d_menu'
    )

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  slides: {
    type: Array as PropType<DeepReadonly<Slide[]>>,
    required: true,
  },
  showing: {
    type: Boolean,
    default: false,
  },
  drillDataSetManager: {
    type: Object as PropType<DrillDataSetManager>,
    required: true,
  },
})

const emit = defineEmits(['update:deck', 'update:showing'])

const loading = ref(false)
const showImageEditorModal = ref(false)
const showFileSelectorModal = ref(false)
const shouldDisableSubmit = ref(false)
const embedUsageWarning = reactive<{
  showing: boolean
  decksAffected: DeckWithEmbed[]
  publicDecks: Deck[]
  cancel(): void
  confirm(): void
}>({
  showing: false,
  decksAffected: [],
  publicDecks: [],
  cancel: () => {},
  confirm: () => {},
})
const drillDataAccessTypeWarning = reactive<{
  showing: boolean
  drillData: Drill[]
  cancel(): void
  confirm(): void
  secondaryAction(): void
}>({
  showing: false,
  drillData: [],
  cancel: () => {},
  confirm: () => {},
  secondaryAction: () => {},
})

const form = reactive<Pick<Omit<Deck, 'title' | 'url_slug'> & IncludeAssets, WriteableKeys>>({
  description: null,
  file_id: -1,
  view_access_type: 'private',
  edit_access_type: 'private',
  press_release_date: null,
  press_release_headline: null,
  type_tags: [],
  assets: [],
  file: null,
  deck_image: null,
  thumbnail: '',
  show_3d_menu: true,
})

const DeckSettingsSchema = z.object({
  title: z
    .string()
    .nonempty(ZodMessages.title)
    .max(255)
    .default(props.deck.title || ''),
  url_slug: z
    .string()
    .nullable()
    .optional()
    .refine(data => !data || !data.trim().length || /^[a-zA-Z0-9-]+$/i.test(data), {
      message: 'The URL slug can only contain alphanumeric characters or a dash.',
    }),
})

const { handleSubmit, defineField, errors } = useForm<z.infer<typeof DeckSettingsSchema>>({
  validationSchema: toTypedSchema(DeckSettingsSchema),
  initialValues: {
    title: props.deck?.title ?? '',
    url_slug: props.deck?.url_slug ?? '',
  },
})

const [formTitle, titleAttrs] = defineField('title')
const [formUrlSlug, urlSlugAttrs] = defineField('url_slug')

const { assets, error: assetsError, loading: assetsLoading } = useCompanyAssets(computed(() => props.deck.company_id))

const { error: shelfError, file, folder, selectFile, setPreviewFile, saveFileEdits, refetch } = useShelf({ deck: toRef(props, 'deck') })

watch(shelfError, e => {
  if (e && !(e instanceof FileRequiredError) && !(e instanceof FolderRequiredError)) {
    $sentry?.captureException(e)
  }
})

watch(assetsError, e => {
  if (e) {
    $sentry?.captureException(e)
  }
})

const modal = ref<any | HTMLElement | undefined>()

watch(
  () => props.showing,
  async (showing: boolean) => {
    shouldDisableSubmit.value = false

    if (showing) {
      form.description = props.deck.description
      form.file_id = props.deck.file_id
      form.show_3d_menu = props.deck.show_3d_menu
      form.view_access_type = props.deck.view_access_type
      form.edit_access_type = props.deck.edit_access_type
      form.press_release_date = props.deck.press_release_date
      form.press_release_headline = props.deck.press_release_headline
      form.type_tags = props.deck.type_tags as DeckTypeTags[]
      form.assets = [...(props.deck as unknown as Deck & IncludeAssets).assets]
      formTitle.value = props.deck.title
      formUrlSlug.value = props.deck.url_slug as string
      await refetch()
    }
  },
  { immediate: true },
)

const deckType = computed({
  get: () => DECK_TYPES.find(type => type.id === form.type_tags[0]) ?? DECK_TYPES[0],
  set: val => {
    form.type_tags = val.id ? [val.id as DeckTypeTags] : []
  },
})

const pressReleaseDate = computed({
  get: () => form.press_release_date && new Date(form.press_release_date),
  set: val => {
    form.press_release_date = val ? val.toISOString() : null
  },
})

function getSelectedAssets(asset: DeepReadonly<Asset>) {
  return form.assets.some(a => a.id === asset.id)
}

function addAsset(asset: DeepReadonly<Asset>) {
  form.assets = [...form.assets, asset as Asset]
}

function removeAsset(asset: DeepReadonly<Asset>) {
  form.assets = [...form.assets, asset as Asset]
  form.assets = form.assets.filter(a => a.id !== asset.id)
}

function handleImageEdits(file: File) {
  setPreviewFile(file)
  showImageEditorModal.value = false
}

function handleFileSelected(file: ShelfFile) {
  selectFile(file)
  showFileSelectorModal.value = false
}

async function onFileUploaded(file: File) {
  await setPreviewFile(file)

  if (!shelfError.value) {
    showImageEditorModal.value = true
  }
}

function _scrollToError(): void {
  const el = document.querySelector('.error')
  if (el) {
    const position = el.getBoundingClientRect()
    const modalElement = modal.value instanceof Node ? modal.value : modal.value?.$el
    modalElement?.scrollTo(position.left, position.top + (modalElement?.scrollTop ?? 0) - 120)
  }
}

async function _saveFile() {
  shouldDisableSubmit.value = true
  try {
    const file = await saveFileEdits(form.view_access_type)
    form.file = file ?? null
    form.file_id = file?.id ?? null
  } finally {
    shouldDisableSubmit.value = false
  }
}

function cancel() {
  emit('update:showing', false)
}

const { hasRole } = useUser()
const isInternalAdmin = computed(() => hasRole('internal_admin'))

async function onUpdatePermissionSettings(view_access_type: 'selected' | 'private' | 'public', event: MouseEvent) {
  if (form.view_access_type === view_access_type) return

  const isSwitchingFromPublicToPrivateOrSelected = form.view_access_type === 'public' && view_access_type !== 'public'
  const isSwitchingFromFromPrivateOrSelectedToPublic = form.view_access_type !== 'public' && view_access_type === 'public'

  if (isSwitchingFromPublicToPrivateOrSelected) {
    event.stopPropagation()
    event.preventDefault()
    await openEmbedWarningDialog(view_access_type)
  } else if (isSwitchingFromFromPrivateOrSelectedToPublic) {
    event.stopPropagation()
    event.preventDefault()
    extractModelIds()
    await openDrillLabelWarningDialog(view_access_type)
  } else {
    form.view_access_type = view_access_type
  }
}

const openEmbedWarningDialog = async (view_access_type: 'selected' | 'private' | 'public') => {
  if (props.deck.id && props.deck.company_id) {
    try {
      loading.value = true
      const decksAffected = await getUsedEmbedDecks({ deckId: props.deck.id, companyId: props.deck.company_id })
      const publicDecks = await getPublicDecks(props.deck.company_id)

      if (decksAffected.length) {
        Object.assign(embedUsageWarning, {
          showing: !!decksAffected.length,
          decksAffected,
          publicDecks,
          confirm: async (deck: Deck) => {
            try {
              await updateAllEmbeds({ decks: decksAffected, deck })
              form.view_access_type = view_access_type
              embedUsageWarning.showing = false
              await update()
            } catch (e) {
              $sentry?.captureException(e)
            }
          },
          cancel: () => {
            embedUsageWarning.showing = false
          },
        })
        return
      }

      form.view_access_type = view_access_type
    } finally {
      loading.value = false
    }
  }
}

const openDrillLabelWarningDialog = async (view_access_type: 'selected' | 'private' | 'public') => {
  try {
    loading.value = true
    const modelIds = extractModelIds()
    const privateOrSecureDrillData: Drill[] = Array.from(
      new Set(
        (await getDrillLabels(modelIds))
          .filter(drill => drill.data_set.view_access_type === 'private' || drill.data_set.view_access_type === 'secure')
          .map(drill => JSON.stringify(drill.data_set)),
      ),
    ).map(x => JSON.parse(x))

    if (privateOrSecureDrillData.length) {
      Object.assign(drillDataAccessTypeWarning, {
        showing: true,
        drillData: privateOrSecureDrillData,
        cancel: () => {
          drillDataAccessTypeWarning.showing = false
        },
        confirm: async () => {
          try {
            await setDrillDataPublic(privateOrSecureDrillData)
            form.view_access_type = view_access_type
            drillDataAccessTypeWarning.showing = false
            await update()
          } catch (e) {
            $sentry?.captureException(e)
          }
        },
        secondaryAction: () => {
          form.view_access_type = view_access_type
          drillDataAccessTypeWarning.showing = false
        },
      })

      return
    }

    form.view_access_type = view_access_type
  } finally {
    loading.value = false
  }
}

async function getDrillLabels(modelIds: number[]) {
  let dataSets: DrillDataSet[] = []
  if (props.deck.company_id) {
    dataSets = (
      await Promise.all(
        modelIds.map(modelId => {
          return props.drillDataSetManager.getDataSets({ modelId, companyId: props.deck.company_id })
        }),
      )
    ).flatMap(x => x)
  }
  return dataSets
}

function extractModelIds() {
  if (!props.slides) return []
  return [...new Set(props.slides.map(s => s.model_id).filter(x => !!x))] as number[]
}

async function setDrillDataPublic(drillData: Drill[]) {
  await Promise.all(
    drillData.map(async drill => {
      await $fetch<any>(`v2/drill/companies/${props.deck.company_id}/data/${drill.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          view_access_type: 'public',
        },
      })
    }),
  )
}

async function getUsedEmbedDecks({ deckId, companyId }: { deckId: string | number; companyId: string | number }) {
  const { results } = await $fetch<any>(`v2/companies/${companyId}/embed/decks`, {
    ...useFetchSharedOptions(),
    params: { deck_id: deckId, include: ['partner_name'] },
  })
  return results
}

async function getPublicDecks(companyId: string | number) {
  const { results } = await $fetch<any>(`v2/companies/${companyId}/decks`, {
    ...useFetchSharedOptions(),
    params: {
      view_access_type: 'public',
      archived: false,
      include: ['slides'],
    },
  })
  return results
}

async function updateAllEmbeds({ decks, deck }: { decks: DeckWithEmbed[]; deck: Deck }) {
  const [start_slide_id, end_slide_id] = getDeckSlideIds(deck)

  await Promise.all(
    decks.map(async (embeddedDeck: DeckWithEmbed) => {
      await $fetch<any>(`v2/companies/${deck.company_id}/embed/decks/${embeddedDeck.id}`, {
        ...useFetchSharedOptions(),
        method: 'PATCH',
        body: {
          start_slide_id,
          end_slide_id,
          company_id: deck.company_id,
          deck_id: deck.id,
        },
      })
    }),
  )
}

function getDeckSlideIds(deck: Deck) {
  const start_slide_id = deck.slides.find(slide => slide.type === 'M')?.id
  const end_slide_id = deck.slides.find(slide => slide.id !== start_slide_id && slide.type === 'M')?.id

  return [start_slide_id, end_slide_id]
}

const update = handleSubmit(async values => {
  try {
    loading.value = true

    await _saveFile()

    if (shelfError.value) {
      _scrollToError()
      return
    }

    // Ensure the edit access type is based on the view access type.
    // Without being set correctly, Editor's list views will have inconsistent behaviour.
    form.edit_access_type = form.view_access_type === 'public' ? 'private' : form.view_access_type

    for (let key in form) {
      const val = form[key as NullableStringKeys]
      if (typeof val === 'string' && val === '') {
        form[key as NullableStringKeys] = null
      }
    }

    const payload = { ...toRaw(form), title: formTitle.value, url_slug: formUrlSlug.value ? formUrlSlug.value : null }
    emit('update:deck', { id: props.deck.id, ...payload })
  } finally {
    loading.value = false
  }
})
</script>

<style lang="stylus" scoped>
.error
  @apply text-red-800

.form-field.gold:deep(.mx-input)
  @apply border-gold-800 hover_border-gold-800
</style>
